import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "New house developer",
  "Modular construction",
  "Luxury residential",
  "Self-build",
  "Leisure / holiday accommodation",
  "Commercial",
  "Other",
];

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  message: "",
};

function useConsultationForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
      toast.success("Thank you. We'll be in touch shortly.");
      setForm(INITIAL_FORM);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return { form, update, loading, done, submit, reset: () => setDone(false) };
}

export default function FinalCTA() {
  const { form, update, loading, done, submit, reset } = useConsultationForm();

  return (
    <section id="consultation" data-testid="cta-section" className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <CTACopy />
          <div className="lg:col-span-6">
            {done ? (
              <SuccessView onReset={reset} />
            ) : (
              <ConsultationForm
                form={form}
                update={update}
                loading={loading}
                onSubmit={submit}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTACopy() {
  return (
    <div className="lg:col-span-6 lg:sticky lg:top-32">
      <h2
        data-testid="cta-headline"
        className="font-display text-4xl sm:text-5xl lg:text-7xl font-extralight tracking-[-0.02em] leading-[1.02] text-ecoh-ink"
      >
        Experience
        <br />
        <span>ecoHeatwave</span>
      </h2>
      <p className="mt-8 max-w-md text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        Discover how ecoHeatwave delivers invisible, intelligent heating for
        modern buildings.
      </p>
      <div className="mt-12" />
      <div className="mt-8 grid grid-cols-2 gap-16 max-w-lg">
        <Meta label="Email" value="info@futurehomesystems.co.uk" />
        <Meta label="Telephone" value="0330 633 2911" />
      </div>
    </div>
  );
}

function SuccessView({ onReset }) {
  return (
    <div
      data-testid="form-success"
      className="rounded-2xl bg-ecoh-surface p-10 md:p-14 border border-ecoh-ink/5"
    >
      <div className="overline">Received</div>
      <h3 className="mt-4 font-display text-3xl md:text-4xl font-light text-ecoh-ink leading-tight tracking-tight">
        Thank you.
        <br />
        <span>We'll be in touch soon.</span>
      </h3>
      <p className="mt-5 text-ecoh-muted font-light">
        A member of the Future Home Systems team will respond to your enquiry shortly.
      </p>
      <button
        data-testid="form-reset"
        onClick={onReset}
        className="mt-8 text-sm text-ecoh-accent hover:text-ecoh-accentHover border-b border-ecoh-accent/40 hover:border-ecoh-accent pb-1"
      >
        Submit another enquiry →
      </button>
    </div>
  );
}

function ConsultationForm({ form, update, loading, onSubmit }) {
  return (
    <form onSubmit={onSubmit} data-testid="consultation-form" className="space-y-8">
      <Field
        id="name"
        label="Name"
        value={form.name}
        onChange={update("name")}
        required
        testid="form-input-name"
      />
      <Field
        id="email"
        label="Email"
        type="email"
        value={form.email}
        onChange={update("email")}
        required
        testid="form-input-email"
      />
      <Field
        id="phone"
        label="Phone (optional)"
        value={form.phone}
        onChange={update("phone")}
        testid="form-input-phone"
      />
      <ProjectTypeField
        value={form.project_type}
        onChange={update("project_type")}
      />
      <MessageField value={form.message} onChange={update("message")} />
      <SubmitRow loading={loading} />
    </form>
  );
}

function ProjectTypeField({ value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-ecoh-muted mb-3">
        Project type
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid="form-select-project-type"
          className="h-12 bg-transparent border-0 border-b border-ecoh-ink/15 rounded-none px-0 text-ecoh-ink font-light hover:border-ecoh-ink/40 focus:ring-0 focus:border-ecoh-accent transition-colors"
        >
          <SelectValue placeholder="Select a project type" />
        </SelectTrigger>
        <SelectContent>
          {PROJECT_TYPES.map((t) => (
            <SelectItem
              key={t}
              value={t}
              data-testid={`project-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MessageField({ value, onChange }) {
  return (
    <div>
      <label
        htmlFor="message"
        className="block text-[11px] tracking-[0.22em] uppercase text-ecoh-muted mb-3"
      >
        Message (optional)
      </label>
      <textarea
        id="message"
        rows={4}
        value={value}
        onChange={onChange}
        data-testid="form-input-message"
        placeholder="Tell us a little about your project…"
        className="w-full bg-transparent border-0 border-b border-ecoh-ink/15 px-0 py-2 text-ecoh-ink placeholder:text-ecoh-ink/35 font-light outline-none focus:border-ecoh-accent transition-colors resize-none"
      />
    </div>
  );
}

function SubmitRow({ loading }) {
  return (
    <div className="pt-4 flex flex-wrap items-center gap-6">
      <button
        type="submit"
        disabled={loading}
        data-testid="form-submit"
        className="group inline-flex items-center gap-3 bg-ecoh-accent hover:bg-ecoh-accentHover text-white px-8 py-4 rounded-full font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
      >
        {loading ? "Sending…" : "Book a consultation"}
        {!loading && (
          <span className="transition-transform group-hover:translate-x-1">→</span>
        )}
      </button>
      <p className="text-xs text-ecoh-muted/80">
        We respect your privacy and never share your details.
      </p>
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] tracking-[0.22em] uppercase text-ecoh-muted mb-3">
        {label}
        {required && <span className="text-ecoh-accent ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-0 border-b border-ecoh-ink/15 px-0 py-2 text-ecoh-ink font-light outline-none focus:border-ecoh-accent transition-colors"
      />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-ecoh-muted">{label}</div>
      <div className="mt-1 text-ecoh-ink text-sm md:text-base">{value}</div>
    </div>
  );
}
